{
    "redo": "Redo Onboarding",
    "title": "Meet Cephable",
    "description": "Meet Cephable, your personal AI assistant and share your interest for personalized use cases.",
    "congrats": "Congrats!",
    "customControls": {
        "tourLabel": "Always Available Global & Device Controls",
        "rowDescription": "Global and device controls are always available if your microphone is on, no matter what app you are using. You can use these controls to interact with your device without having to create new app controls. They contain helpful commands like swapping controls or apps, generating text, pausing your microphone and more."
    },
    "controlExpanded": {
        "tourLabel": "Control",
        "description": "App controls are configured sets of controls that you can use on a desired application. This section shows you all the available controls you can use through voice commands, tapping, typing, and more."
    },
    "profile": {
        "switch": {
            "title": "Switch App Controls",
            "description": "Your app controls can switch automatically based on the app you are using, or you can change them manually here anytime."
        }
    },
    "stopListening": {
        "title": "Try saying `Stop Listening`",
        "description": "This pauses your microphone without turning it off completely. Say `Start Listening` again whenever you are ready."
    },
    "dictation": {
        "tourLabel": "Dictation",
        "learn": "Learn how to use dictation mode to convert your speech to text hands-free.",
        "welcome": {
            "title": "Dictation",
            "description": "Dictation lets you speak naturally and have your words typed while you talk. It's like having a personal assistant who types everything you say."
        },
        "microphone": {
            "title": "Turn on Your Microphone",
            "description": "First, turn on your microphone. Click the Microphone Toggle on the Cephable Panel."
        },
        "activate": {
            "title": "Start Dictation Mode",
            "description": "Voice starts in command mode. To start dictation, say \"Start dictation\" or click the Dictation button. Lets turn on dictation mode now."
        },
        "focus": {
            "title": "Focus on a Text Field or Document",
            "description": "Dictation works best when you focus on a text field or document. Click on a text field or document where you want to dictate."
        },
        "speaking": {
            "title": "Start Speaking",
            "description": "Now you can start speaking. Everything you say will be typed in the text field or document you focused on. Try saying something like \"Hello, this is a test of dictation mode.\""
        },
        "pauseResume": {
            "title": "Pausing Dictation",
            "description": "Need a break without turning off dictation? Just say \"Stop Listening\". You can resume anytime with \"Start dictation.\" again. You can customize this further with your always on global and device controls."
        },
        "stop": {
            "title": "Stopping Dictation",
            "description": "Need to switch back to your app controls?  Just say \"Stop dictation\". This will stop dictation mode and return you to command mode. You can resume anytime with \"Start dictation.\" again."
        },
        "complete": {
            "description": "That's it! You can now dictate, pause, resume, and swap between dictation and command modes. Dictation is a powerful tool for hands-free typing. Use it to write emails, documents, or anything else you need to type out loud."
        }
    },
    "editingVideos": {
        "tourLabel": "Focus on the Story, Not the Tedious Clicks for Video Editing",
        "learn": "Learn how to use voice controls and dictation to edit videos faster, and remove the cognitive load of remembering which buttons to click while editing.",
        "tryOpen": {
            "title": "Try saying `Open CapCut` or `Open iMovie`."
        },
        "complete": {
            "description": "You now know how to use voice commands and quick actions to play, pause, skip tracks, and adjust volume in your favorite video editing apps. Try exploring more commands or customizing your own quick actions for even smoother video control."
        }
    },
    "fpsGamePlay": {
        "tourLabel": "Play FPS Games with Voice Controls",
        "learn": "Learn how to use voice controls and quick actions to play FPS games.",
        "tryOpen": {
            "title": "Try saying `Open Valorant` or `Open Battlefield`"
        },
        "complete": {
            "description": "You now know how to use voice commands and quick actions to move, stop, and control your character in FPS games. Try exploring more commands or customizing your own quick actions for even smoother and faster gameplay."
        }
    },
    "extensions": {
        "tourLabel": "Extensions",
        "rowDescription": "Extend Cephable with more ways to control your device — like Camera Controls for face and head movement, or gamepad support. Turn on the ones you need here."
    },
    "microphone": {
        "tourLabel": "Voice Controls",
        "learn": "Learn how you can use your voice to control any app and use dictation to type what you say.",
        "rowDescription": "When the microphone is off, we will not listen to your voice commands at all but if you turn it on, you will be able to use global commands to toggle pause, start, and more. Let's turn it on.",
        "tryOpen": {
            "title": "Try saying `Open Spotify` or `Open Word`"
        },
        "finishedCommands": {
            "title": "Excellent! You've learned the basics of Voice Controls.",
            "description": "You can always revisit the tour from the Support Page. The next step is to learn about Dictation. You can start the tour from the support page."
        }
    },
    "smartTasks": {
        "generate": {
            "focusApp": {
                "title": "Focus on the App",
                "description": "Make sure you are focused on the app where you want the generated text to appear. You will be able to seamlessly generate text into that location when you click Generate."
            }
        },
        "rewrite": {
            "focusApp": {
                "title": "Focus on the App",
                "description": "Make sure you are focused on the app where you want the rewritten text to appear. You will be able to seamlessly generate text into that location when you click Generate."
            }
        }
    },
    "tour": {
        "startTour": "Start Tour",
        "finishTour": "Finish Tour",
        "statusDialog": "Please check the panel for more information.",
        "step": "Step {{step}} of {{total}}",
        "skipTour": "Skip Tour"
    },
    "support": {
        "title": "Support",
        "description": "You can visit our support hub for more information on how to use Cephable, including more tours, step-by-step guides, tutorials, and videos."
    },
    "onboarding": {
        "overview": "Overview",
        "learn": "Learn about the features of Cephable and how to use them.",
        "start": "Looks like you are just getting started. Here are some areas that will help you along the way.",
        "dashboard": {
            "title": "Cephable Workspace",
            "description": "The Cephable Workspace is your central hub for managing your device, app controls, and settings. From here, you can customize your experience, view active commands, and access all the features of Cephable."
        },
        "step1": {
            "title": "Welcome to Cephable",
            "description": "Cephable is your personal AI — built for fast actions, voice commands, content generation, and smart automation across your tools. Out-of-the-box controls. No cloud processing. Just productivity that moves with you."
        },
        "finish": {
            "continueToApp": "Continue to Cephable Dashboard",
            "keepExploring": "Keep Exploring"
        },
        "useCaseSlider": {
            "context": "I'd like to use",
            "control": "to control"
        }
    },
    "panel": {
        "description": "This is the panel which is the main control center. It is always visible and allows you to control your apps using your voice, and more. Let's start with the microphone."
    },
    "submit": {
        "title": "Submit",
        "description": "The content will be outputted to where your cursor focus is."
    },
    "tryOpen": {
        "description": "`Open` is a global command that will open the app you specify and send focus to it. If you have automatic control switching enabled, it will also switch to the app controls that best match the app you are using."
    },
    "modeSelector": {
        "title": "Select Mode",
        "description": "If available, you can select the mode in which I will operate. Use Control for the app you want to send commands through, Create for generative text content like summarize, rewrite, translate. Select Automate for desktop or cross-app flows."
    },
    "contextSelector": {
        "title": "Select Context",
        "description": "If available, you can select the context in which I will operate. For example, I can use your clipboard or highlighted text, and you can also attach files or folders you would like me to work with."
    },
    "panelInput": {
        "title": "What do you want me to do?",
        "description": "You can command, type, or dictate what you would like me to do."
    },
    "figma": {
        "tourLabel": "Voice Commands for Figma and Canva",
        "learn": "Learn how to use Controls voice controls, voice mouse, dictation, and AI content generation to improve your design workflow.",
        "tryOpen": {
            "title": "Try saying `Open Figma` or `Open Canva`"
        },
        "complete": {
            "description": "You now know how to Control and Create designs to enhance your workflow. Try exploring more commands for even smoother design control."
        }
    },
    "voiceMouse": {
        "title": "Voice Mouse",
        "description": "For clicking buttons by name, you can use Voice Mouse. This can be used by saying `tap save` or `go to settings`."
    },
    "documentSummary": {
        "tourLabel": "Draft Team Summaries from Confidential Reports",
        "learn": "Automate reading through department reports, identify the right people to inform, and draft an email — entirely on-device, so your confidential data stays private.",
        "congrats": {
            "description": "You now know how to use AI Workflows to read through confidential documents and draft a summary email to your team — all without your data leaving your device."
        }
    },
    "developerBuildTests": {
        "tourLabel": "Automate Builds and Tests with AI Workflows",
        "learn": "Cephable can run multi-step terminal tasks — like building your project and running its tests — entirely on-device, in the background, while you keep working.",
        "complete": {
            "description": "You now know how to run on-device AI Workflows for multi-step build-and-test tasks, respond to permission requests, and keep iterating on the results. Try automating another repetitive dev task."
        }
    },
    "classroomStudyTools": {
        "tourLabel": "Turn Classroom Notes and Articles into Study Tools and Teaching Materials",
        "learn": "Cephable supports teachers and students with dictation, on-device Create tools, and Automate — right in the apps you already use, keeping everything private and on your device.",
        "complete": {
            "description": "You now know how to dictate directly into your documents, generate new study materials from your notes, and use Automate to create full assignments in the background — all on-device."
        }
    },
    "engineeringFlows": {
        "tourLabel": "Voice Controls + AI Automations for Developers",
        "learn": "Learn how to speed up your engineering workflow with voice-controlled dev commands, AI Rewrite using your own code as context, and background AI Workflows — all running on-device.",
        "tryOpen": {
            "title": "Try saying `Open Visual Studio Code`"
        },
        "complete": {
            "description": "You now know how to use voice-controlled dev commands, AI Rewrite with your own code as context, and background AI Workflows to speed up your engineering flow."
        }
    },
    "meetingNotesToFiles": {
        "tourLabel": "Turn Meeting Notes into Actionable Files",
        "learn": "Cephable can break your meeting notes into smaller, actionable files you can work through at your own pace — entirely on-device.",
        "complete": {
            "description": "You now know how to use AI Workflows to break meeting notes into smaller actionable files and pick the conversation back up anytime from AI History."
        }
    },
    "notesToPresentation": {
        "tourLabel": "Summarize Confidential Notes and Build a Presentation, All On-Device",
        "learn": "Cephable can summarize your confidential reports and notes, then use AI Workflows to do it across a whole folder of files — and even build a presentation from the results. All of it runs on-device.",
        "complete": {
            "description": "You now know how to summarize confidential documents, batch-summarize a whole folder with AI Workflows, and turn the results into a presentation — all without your data leaving your device."
        }
    },
    "organizeDownloads": {
        "tourLabel": "Organize Files, Clean Up, and Find What You Lost — with AI Workflows",
        "learn": "Automate can handle multi-step file management tasks — copying, deleting, and finding files — right from a simple prompt.",
        "complete": {
            "description": "You now know how to use AI Workflows to organize files, safely clean up with permission checks, and find files you can't remember the location of."
        }
    },
    "outlookEmailProductivity": {
        "tourLabel": "Speed Up Email in Outlook with Dictation, Quick Actions, and AI",
        "learn": "Cephable Plus and Professional include intelligent Dictation and Create tools for generating and editing content — use them right inside Outlook to reply and draft emails faster.",
        "complete": {
            "description": "You now know how to reply to emails with dictation, use your own Quick Type actions, and use Summarize and Generate to draft and refine email content — all on-device."
        }
    },
    "customizingGameControls": {
        "tourLabel": "Play RPG Games with Voice Controls",
        "learn": "Learn how to use voice controls and quick actions to play RPG games.",
        "tryOpen": {
            "title": "Try saying `Open Minecraft`"
        },
        "complete": {
            "description": "You now know how to use voice commands and quick actions to move, stop, and control your character in RPG games. Try exploring more commands or customizing your own quick actions for even smoother and faster gameplay."
        }
    },
    "voiceToTextSocialMedia": {
        "tourLabel": "Voice to Text for LinkedIn, Slack, and Discord",
        "learn": "Use Intelligent Dictation to speak a post naturally, then Rewrite to adjust its tone — works the same way across LinkedIn, Slack, Discord, and more.",
        "complete": {
            "description": "You now know how to dictate a post and refine it with Rewrite — across LinkedIn, Slack, Discord, and more."
        }
    },
    "aiDocumentTranslation": {
        "tourLabel": "Translate Documents Into Any Language, On-Device",
        "learn": "Cephable can translate a document into one language at a time, or use AI Workflows to translate it into several languages at once — all running locally.",
        "complete": {
            "description": "You now know how to translate a document into a single language with dictation, or into several languages at once with AI Workflows — all on-device."
        }
    },
    "excelAiAutomation": {
        "tourLabel": "Analyze Spreadsheets with Private, On-Device AI",
        "learn": "Cephable can read through your Excel files, answer questions about the numbers, and keep going deeper — all without your data leaving your device.",
        "complete": {
            "description": "You now know how to use AI Workflows to question your spreadsheets, dig deeper with follow-ups, and save a summary — all running on-device."
        }
    },
    "handsFreeComputerControl": {
        "tourLabel": "Control Your Computer with Voice and Camera",
        "learn": "Cephable lets you control your computer hands-free using voice commands, Voice Mouse, and camera-based head and face controls.",
        "tryOpenWord": {
            "title": "Try Saying `Open Word`"
        },
        "openExtensions": {
            "title": "Open the Extensions Tab",
            "description": "To turn on camera controls, open the Extensions tab in the main window."
        },
        "turnOnCamera": {
            "title": "Turn on Camera Controls",
            "description": "Toggle Camera on. A camera icon appears next to your microphone icon, and a new Camera section appears under Settings."
        },
        "cameraInPanel": {
            "title": "Camera Controls in Your Panel",
            "description": "Now that Camera Controls is on, the camera toggle appears in your Cephable panel too — Cephable turns it on for you here."
        },
        "openCameraSettings": {
            "title": "Open Camera Settings",
            "description": "Open Camera Settings to fine-tune how head and face controls work for you."
        },
        "chooseHeadMouseMode": {
            "title": "Choose a Head-Mouse Mode",
            "description": "Pick how head movement controls your mouse: Steady Glide, Speed Boost, or Dynamic Glide."
        },
        "complete": {
            "description": "You now know how to control your computer with voice, Voice Mouse, and camera-based head and face commands. Explore Camera Settings further to build controls that fit you."
        }
    },
    "handsFreeTypingDocumentCreation": {
        "tourLabel": "Create and Edit Documents Hands-Free",
        "learn": "Use Generate, Rewrite, and voice controls together to create and edit a document — like an invoice — almost entirely hands-free.",
        "complete": {
            "description": "You now know how to generate, rewrite, and edit a document almost entirely hands-free using Generate, Rewrite, and voice commands."
        }
    },
    "webResearchCreateHtml": {
        "tourLabel": "Research the Web and Build a Site from It — All On-Device",
        "learn": "Cephable can browse and analyze competitor websites, then use what it finds to generate a new HTML site for you — entirely on-device, so your research stays private.",
        "complete": {
            "description": "You now know how to use AI Workflows to research competitor websites and generate a new HTML site from the findings — all without your data leaving your device."
        }
    },
    "webResearchSummarize": {
        "tourLabel": "Research Regulations and Summarize the Findings — All On-Device",
        "learn": "Cephable can browse the web to research changes like new data protection laws, then summarize what it found into something your team can act on — entirely on-device, so your research stays private.",
        "complete": {
            "description": "You now know how to use AI Workflows to research regulatory changes, dig deeper into implementation, and draft the findings as an email your team can iterate on — all without your data leaving your device."
        }
    }
}
